import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils';
import { AddBreadcrumbItem } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.types';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Params {
	dealId: string | undefined;
	mipId: string | undefined;
	programmeName: string;
}

export const useMipManageBreadcrumbsPrefix = ({
	dealId,
	mipId,
	programmeName,
}: Params): AddBreadcrumbItem[] =>
	useMemo(
		() => [
			{
				name: programmeName,
				url: mipManagePath(MIP_MANAGE_ROUTES.INVESTORS, {
					dealId: dealId ?? '',
					mipId: mipId ?? '',
				}),
				hideInAppHeader: true,
			},
		],
		[dealId, mipId, programmeName]
	);

interface DetailParams {
	dealId: string;
	mipId: string;
	programmeName: string;
	/** Name of the last crumb, e.g. "Investor details" or "Vehicles". */
	leafName: string;
	leafDataTestId?: string;
}

export const useMipManageInvestorDetailBreadcrumbs = ({
	dealId,
	mipId,
	programmeName,
	leafName,
	leafDataTestId,
}: DetailParams): void => {
	const tMipManage = useTranslationMipManage();
	const { search } = useLocation();
	const prefix = useMipManageBreadcrumbsPrefix({ dealId, mipId, programmeName });

	useBreadcrumbs([
		...prefix,
		{
			name: tMipManage('tabs.investors'),
			url: `${mipManagePath(MIP_MANAGE_ROUTES.INVESTORS, { dealId, mipId })}${search}`,
			hideInAppHeader: true,
			dataTestId: TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_BREADCRUMB,
		},
		{
			name: leafName,
			url: '',
			noLink: true,
			hideInAppHeader: true,
			dataTestId: leafDataTestId,
		},
	]);
};

/** Investment details crumbs; the Investments crumb keeps the table's filters and sort. */
export const useMipManageInvestmentDetailBreadcrumbs = ({
	dealId,
	mipId,
	programmeName,
}: Params) => {
	const tMipManage = useTranslationMipManage();
	const { search } = useLocation();
	const prefix = useMipManageBreadcrumbsPrefix({ dealId, mipId, programmeName });

	const items = useMemo<AddBreadcrumbItem[]>(
		() => [
			...prefix,
			{
				name: tMipManage('investment_details.breadcrumbs.investments'),
				url: `${mipManagePath(MIP_MANAGE_ROUTES.INVESTMENTS, {
					dealId: dealId ?? '',
					mipId: mipId ?? '',
				})}${search}`,
				hideInAppHeader: true,
			},
			{
				name: tMipManage('investment_details.breadcrumbs.investment_details'),
				url: '',
				noLink: true,
				hideInAppHeader: true,
			},
		],
		[prefix, dealId, mipId, search, tMipManage]
	);

	useBreadcrumbs(items);
};
